import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";

export default function ResetPasswordPage() {
  const { updatePassword } = useAdminAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const result = await updatePassword(password);
    setSubmitting(false);
    if (result.success) {
      setDone(true);
    } else {
      setError(result.error ?? "Unable to reset password. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex bg-background-950">
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-900 via-background-950 to-primary-900" />
        <div className="relative z-10 flex items-center gap-3">
          <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-white font-extrabold text-lg">
            L+
          </span>
          <div className="leading-tight">
            <p className="text-white font-bold text-xl tracking-wide">L+TV TÉLÉVISION</p>
            <p className="text-background-300 text-xs uppercase tracking-widest">Administration</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            Set a new password for your account.
          </h1>
          <p className="mt-4 text-background-300 text-base leading-relaxed">
            Choose a strong password you don't use anywhere else.
          </p>
        </div>

        <p className="relative z-10 text-background-400 text-sm">
          © {new Date().getFullYear()} L+TV TÉLÉVISION — Edea, Cameroon
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-white font-extrabold">
              L+
            </span>
            <div className="leading-tight">
              <p className="font-bold text-foreground-900">L+TV TÉLÉVISION</p>
              <p className="text-[11px] uppercase tracking-widest text-foreground-500">Administration</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-foreground-900">Set new password</h2>
          <p className="mt-1 text-sm text-foreground-600">
            {done ? "Your password has been updated." : "Enter and confirm your new password."}
          </p>

          {done ? (
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-2 px-3 py-3 bg-green-50 border border-green-200 rounded-md">
                <i className="ri-checkbox-circle-line text-green-600 text-sm mt-0.5" />
                <p className="text-sm text-green-700">
                  Password updated successfully. You can now sign in with your new password.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/admin/login")}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white font-semibold px-5 py-3 rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                Go to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-11 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-foreground-400"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={showPassword ? "ri-eye-off-line text-base" : "ri-eye-line text-base"} />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  Confirm new password
                </label>
                <input
                  id="confirm"
                  type={showPassword ? "text" : "password"}
                  name="confirm"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-50 border border-accent-200 rounded-md">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5" />
                  <p className="text-sm text-accent-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white font-semibold px-5 py-3 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {submitting && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                Update Password
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}